import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { supabase } from './services/supabase';
import { checkSession } from './store/slices/authSlice';
import { RootState, AppDispatch } from './store';
import googleAnalytics from './services/googleAnalyticsService';
import { errorLoggingService } from './services/errorLoggingService';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './components/dashboard-legacy/DashboardPage';
import RankingsPage from './pages/rankings/RankingsPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import KeywordResearchPage from './pages/keyword-research/KeywordResearchPage';
import UrlManagementPage from './pages/url-management/UrlManagementPage';
import CompetitorsPage from './pages/competitors/CompetitorsPage';
import ReportsPage from './pages/reports/ReportsPage';
import ProfilePage from './pages/profile/ProfilePage';
import SettingsPage from './pages/settings/SettingsPage';
import NotFoundPage from './pages/errors/NotFoundPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { RankingOnDemandPage } from './pages/ranking-on-demand/RankingOnDemandPage';
import { GenerativeSearchPage } from './pages/generative-search/GenerativeSearchPage';
import MetaDescriptionPage from './pages/meta-description/MetaDescriptionPage';
import AIRoastPage from './pages/ai-roast/AIRoastPage';
import { PAATreesPage } from './pages/paa-trees/PAATreesPage';
import { PAATreeViewer } from './pages/paa-trees/PAATreeViewer';
import SchemaGeneratorPage from './pages/schema/SchemaGeneratorPage';
import { ContentProjectsPage } from './pages/content-projects/ContentProjectsPage';
import { ProjectResearchHub } from './pages/content-projects/ProjectResearchHub';
import DynamicContentPage from './pages/dynamic-content/DynamicContentPage';
import { GlobalDashboardPage } from './pages/global-dashboard/GlobalDashboardPage';
import { ContentOptimizer } from './components/content-optimizer/ContentOptimizer';

// New unified pages
import { KeywordIntelligencePage } from './pages/keyword-intelligence/KeywordIntelligencePage';
import { ContentStudioPage } from './pages/content-studio/ContentStudioPage';
import { PerformanceHubPage } from './pages/performance-hub/PerformanceHubPage';
import { ErrorDashboardPage } from './pages/error-dashboard/ErrorDashboardPage';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Google Analytics Route Tracker
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    googleAnalytics.trackPageView(location.pathname, document.title);
  }, [location]);
  
  return null;
};

// App wrapper component that provides context and theme
const AppWrapper: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useSelector((state: RootState) => state.ui);
  const { user } = useSelector((state: RootState) => state.auth);

  // Create theme based on dark mode preference
  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#4e6bff', // Primary blue
      },
      secondary: {
        main: '#f7b731', // Secondary yellow
      },
      error: {
        main: '#d61f26', // Error red
      },
      success: {
        main: '#28a745', // Success green
      },
    },
    typography: {
      fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
      },
      h2: {
        fontWeight: 700,
      },
      h3: {
        fontWeight: 600,
      },
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 500,
      },
      h6: {
        fontWeight: 500,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
    },
  });
  
  useEffect(() => {
    // Initialize Google Analytics
    googleAnalytics.initialize();

    // Initialize Error Logging Service
    errorLoggingService.initialize(user?.email || undefined);

    // Check for existing Supabase session
    dispatch(checkSession());
  }, [dispatch]);

  // Update error logging service when user changes
  useEffect(() => {
    errorLoggingService.setUserEmail(user?.email || null);
  }, [user?.email]);
  
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <SnackbarProvider maxSnack={3}>
          <CssBaseline />
          <RouteTracker />
          <Outlet />
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

// Create the router with future flags
const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <AppWrapper />,
      children: [
        // Public routes
        { path: "login", element: <LoginPage /> },
        { path: "register", element: <RegisterPage /> },
        
        // Protected routes
        {
          path: "/",
          element: <ProtectedRoute><MainLayout /></ProtectedRoute>,
          children: [
            // Default redirect to Keyword Intelligence (fast load)
            { index: true, element: <Navigate to="/keyword-intelligence" /> },

            // NEW UNIFIED PAGES
            { path: "performance-hub", element: <PerformanceHubPage /> },
            { path: "keyword-intelligence", element: <KeywordIntelligencePage /> },
            { path: "content-studio", element: <ContentStudioPage /> },

            // Standalone tools (kept as-is)
            { path: "schema-generator", element: <SchemaGeneratorPage /> },
            { path: "dynamic-content", element: <DynamicContentPage /> },
            { path: "generative-search", element: <GenerativeSearchPage /> },

            // BACKWARDS COMPATIBILITY - Redirect old routes to new unified pages
            { path: "dashboard", element: <Navigate to="/performance-hub" replace /> },
            { path: "rankings", element: <Navigate to="/performance-hub" replace /> },
            { path: "analytics", element: <Navigate to="/performance-hub" replace /> },
            { path: "keyword-research", element: <Navigate to="/keyword-intelligence" replace /> },
            { path: "ranking-on-demand", element: <Navigate to="/keyword-intelligence" replace /> },
            { path: "competitors", element: <Navigate to="/keyword-intelligence" replace /> },
            { path: "paa-trees", element: <Navigate to="/keyword-intelligence" replace /> },
            { path: "content-optimizer", element: <Navigate to="/content-studio" replace /> },
            { path: "content-projects", element: <Navigate to="/content-studio" replace /> },
            { path: "meta-descriptions", element: <Navigate to="/content-studio" replace /> },
            { path: "ai-roast", element: <Navigate to="/content-studio" replace /> },

            // Keep these routes for deep linking (project details, PAA tree viewer)
            { path: "paa-trees/:treeId", element: <PAATreeViewer /> },
            { path: "content-projects/:projectId", element: <ProjectResearchHub /> },

            // Keep legacy dashboard for reference
            { path: "dashboard-legacy", element: <DashboardPage /> },

            // Utility pages
            { path: "url-management", element: <UrlManagementPage /> },
            { path: "reports", element: <ReportsPage /> },
            { path: "profile", element: <ProfilePage /> },
            { path: "settings", element: <SettingsPage /> },

            // Admin pages
            { path: "error-dashboard", element: <ErrorDashboardPage /> },
          ],
        },
        
        // 404 page
        { path: "*", element: <NotFoundPage /> },
      ],
    },
  ]
);

const App: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default App;